import {Anchor, Button, Group, Stack, Text, Tooltip, createStyles} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';

interface NewPredictionTopBarProps {
  autoName: string;
  // Number of prediction batches that will be created (one per selected model).
  batchCount: number;
  disabledReason: string | null;
  isSubmitting: boolean;
  batchName: string;
  onBack: () => void;
  onBatchNameChange: (name: string) => void;
  onSubmit: () => void;
  // Total predictions across all batches (inputs × models).
  predictionCount: number;
  // The name each batch will be created with, already resolved by the same helper submit uses. Empty
  // with 0–1 models selected, where the field itself already shows the resulting name.
  resolvedBatchNames: ReadonlyArray<string>;
}

export default function NewPredictionTopBar({
  autoName,
  batchCount,
  disabledReason,
  isSubmitting,
  batchName,
  onBack,
  onBatchNameChange,
  onSubmit,
  predictionCount,
  resolvedBatchNames,
}: NewPredictionTopBarProps): JSX.Element {
  const {classes} = useStyles();
  const batchNameLabelId = useId();

  return (
    <div className={classes.bar}>
      <div className={classes.left}>
        <Anchor className={classes.backLink} component="button" onClick={onBack} size="md" type="button">
          <Group noWrap={true} spacing={6}>
            <BenchlingIcon className={classes.backIcon} icon="arrow-left" />
            Back
          </Group>
        </Anchor>
      </div>
      <Stack className={classes.batchNameInput} spacing={4}>
        <Text className="sr-only" component="span" id={batchNameLabelId}>
          Batch name
        </Text>
        <EditableTextField
          dataTestAttrs={{component: 'NewPredictionTopBar', element: 'batch-name'}}
          emptyValueAllowed={true}
          inputClassName={classes.batchNameField}
          isFullWidth={true}
          labelledBy={batchNameLabelId}
          maxLength={255}
          placeholder={autoName}
          requestChange={onBatchNameChange}
          textPlaceholder={autoName}
          value={batchName}
          viewerClassName={classes.batchNameViewer}
        />
        {resolvedBatchNames.length > 0 && (
          <div aria-label="Batch names" role="group">
            <Text className={classes.resolvedBatchName} color="dimmed" size="xs">
              {resolvedBatchNames.join(' • ')}
            </Text>
          </div>
        )}
      </Stack>
      <Group className={classes.right} noWrap={true} spacing="md">
        {predictionCount > 0 && (
          <Text color="dimmed" size="sm">
            {batchCount > 1
              ? `${predictionCount} ${pluralize(
                  predictionCount,
                  'prediction'
                )} across ${batchCount} ${pluralize(batchCount, 'batch', 'batches')} expected`
              : `${predictionCount} ${pluralize(predictionCount, 'prediction')} expected`}
          </Text>
        )}
        <Tooltip disabled={disabledReason == null} label={disabledReason ?? undefined}>
          <span>
            <Button
              disabled={disabledReason != null}
              leftIcon={<BenchlingIcon icon="paper-plane" />}
              loading={isSubmitting}
              onClick={onSubmit}
              size="md"
            >
              Submit
            </Button>
          </span>
        </Tooltip>
      </Group>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  bar: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing.md,
    // Equal-width side columns keep the center column visually centered regardless of how
    // much content sits on either side. minmax(0, 1fr) lets the sides shrink without overflow.
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  },
  left: {
    justifySelf: 'start',
    minWidth: 0,
  },
  right: {
    justifySelf: 'end',
    minWidth: 0,
  },
  backLink: {
    whiteSpace: 'nowrap',
  },
  backIcon: {
    fontSize: theme.fontSizes.md,
  },
  batchNameInput: {
    maxWidth: 560,
    width: '50vw',
  },
  batchNameField: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 500,
  },
  batchNameViewer: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 500,
  },
  // Names can run to 255 characters, so cap the preview at two lines within the center column.
  resolvedBatchName: {
    display: '-webkit-box',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    whiteSpace: 'normal',
  },
}));
